import { apiClient } from './client';

export interface CampaignAsset {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  url: string | null;
  kind: string;
  sector: string | null;
  signalTypes: string[];
  nurtureEligible: boolean;
  active: boolean;
}

export interface SignalOption {
  id: string;
  label: string;
  weight: number;
}

export interface CampaignOption {
  externalId: string;
  name: string;
  status: string | null;
}

export interface CampaignAssetsResponse {
  assets: CampaignAsset[];
  signals: SignalOption[];
  campaigns: CampaignOption[];
}

export interface CampaignAssetInput {
  title: string;
  slug: string;
  url: string;
  description?: string | null;
  kind?: string;
  sector?: string | null;
  signalTypes?: string[];
  nurtureEligible?: boolean;
}

export async function getCampaignAssets(): Promise<CampaignAssetsResponse> {
  return apiClient.get('/resources/campaign-assets');
}

export async function createCampaignAsset(
  input: CampaignAssetInput,
): Promise<{ asset: CampaignAsset }> {
  return apiClient.post('/resources/campaign-assets', input);
}

export async function updateCampaignAsset(
  id: string,
  input: Partial<CampaignAssetInput> & { active?: boolean },
): Promise<{ asset: CampaignAsset }> {
  return apiClient.patch(`/resources/campaign-assets/${id}`, input);
}

export async function deactivateCampaignAsset(id: string): Promise<{ success: boolean }> {
  return apiClient.delete(`/resources/campaign-assets/${id}`);
}

/** Where the link is going out. Only channels that can actually carry a UTM. */
export const CAMPAIGN_SOURCES = [
  { id: 'lemlist', label: 'Lemlist', medium: 'email' },
  { id: 'linkedin', label: 'LinkedIn outbound', medium: 'social' },
  { id: 'newsletter', label: 'Newsletter', medium: 'email' },
  { id: 'manual', label: 'Manual / one-off email', medium: 'email' },
] as const;

export type CampaignSourceId = (typeof CAMPAIGN_SOURCES)[number]['id'];

/** lemlist campaign names are free text; UTM values should not be. */
export function slugifyCampaign(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/**
 * The tagged link a rep pastes into a sequence.
 *
 * `utm_content` is the asset slug and `utm_term` the signal, so a click answers
 * both "which asset worked" and "which trigger was worth writing about". Both
 * are read on the way in already — RudderStack from site behaviour, Fillout from
 * form submissions into Attio — so nothing new has to run for this to be
 * attributed. Existing query parameters on the asset URL are preserved.
 */
export function buildCampaignLink(opts: {
  url: string;
  assetSlug: string;
  source: CampaignSourceId;
  medium: string;
  campaignName?: string | null;
  signalId?: string | null;
}): string {
  let parsed: URL;
  try {
    parsed = new URL(opts.url);
  } catch {
    return opts.url;
  }
  parsed.searchParams.set('utm_source', opts.source);
  parsed.searchParams.set('utm_medium', opts.medium);
  parsed.searchParams.set('utm_content', opts.assetSlug);
  if (opts.campaignName) {
    parsed.searchParams.set('utm_campaign', slugifyCampaign(opts.campaignName));
  }
  if (opts.signalId) parsed.searchParams.set('utm_term', opts.signalId);
  return parsed.toString();
}
